import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const DEFAULT_CAMERA_OPTION = { value: 'all', label: 'All cameras' };

const FallEventsFilterBar = ({
  statusFilter,
  periodFilter,
  cameraFilter,
  onStatusChange,
  onPeriodChange,
  onCameraChange,
  onReset,
  statusOptions,
  periodOptions,
  cameraOptions = [],
  totalCount,
}) => {
  const [cameraDialogOpen, setCameraDialogOpen] = React.useState(false);
  const [cameraSearch, setCameraSearch] = React.useState('');
  const options = React.useMemo(
    () => (cameraOptions.length ? cameraOptions : [DEFAULT_CAMERA_OPTION]),
    [cameraOptions],
  );

  const selectedCameraLabel = React.useMemo(() => {
    const selected = options.find((option) => option.value === cameraFilter);
    return selected ? selected.label : DEFAULT_CAMERA_OPTION.label;
  }, [options, cameraFilter]);

  const filteredCameraOptions = React.useMemo(() => {
    if (!cameraSearch.trim()) {
      return options;
    }
    const needle = cameraSearch.trim().toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, cameraSearch]);

  const handleCameraSelect = (value) => {
    onCameraChange?.(value);
    setCameraDialogOpen(false);
    setCameraSearch('');
  };

  const closeDialog = () => {
    setCameraDialogOpen(false);
    setCameraSearch('');
  };

  return (
    <Stack spacing={2} sx={{ mb: 3 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={statusFilter}
              onChange={(event) => onStatusChange?.(event.target.value)}
              displayEmpty
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={periodFilter}
              onChange={(event) => onPeriodChange?.(event.target.value)}
              displayEmpty
            >
              {periodOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ minWidth: 200 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCameraDialogOpen(true)}
              sx={{
                justifyContent: 'space-between',
                width: '100%',
                textTransform: 'none',
                py: 1,
                px: 1.5,
              }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Camera
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {selectedCameraLabel}
                </Typography>
              </Box>
            </Button>
          </Box>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {totalCount} event{totalCount === 1 ? '' : 's'}
          </Typography>
          <Button
            variant="text"
            onClick={onReset}
            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
          >
            Reset filters
          </Button>
        </Stack>
      </Stack>

      <Dialog
        open={cameraDialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select a camera</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search cameras"
            value={cameraSearch}
            onChange={(event) => setCameraSearch(event.target.value)}
            sx={{ mb: 2 }}
          />
          <List dense disablePadding>
            {filteredCameraOptions.map((option) => (
              <ListItemButton
                key={option.value}
                selected={option.value === cameraFilter}
                onClick={() => handleCameraSelect(option.value)}
              >
                <ListItemText primary={option.label} />
              </ListItemButton>
            ))}
            {!filteredCameraOptions.length && (
              <Box sx={{ px: 1, py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No cameras match your search.
                </Typography>
              </Box>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={() => handleCameraSelect(DEFAULT_CAMERA_OPTION.value)}>
            Show all
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default FallEventsFilterBar;
